import { useEffect, useRef } from 'react'
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react'
import { Theme, styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import GlobalStyles from '@mui/material/GlobalStyles'
import { neutral } from '@/theme/colors'
import { APP_FRAME_GAP_PROPERTY } from '@/constants/appFrame'
import { APP_SCROLL_CONTAINER_ATTR } from '@/utils/scrollAppToTop'
import { usePathname } from '@/hooks/use-pathname'
import type { NavColor } from '@/types/settings'
import type { Section } from '../config'
import { MobileNav } from '../mobile-nav'
import { SideNav } from './side-nav'
import { TopNav } from './top-nav'
import { useMobileNav } from './use-mobile-nav'
import {
  SIDE_NAV_DRAWER_EXPANDED_PX,
  SIDE_NAV_DRAWER_FOLDED_PX,
} from './side-nav-layout-dimensions'
import { isDocumentsEditorSidenavPath } from '@/routes/documents-editor-paths'
import type { ChangelogReleasesState } from '@/components/WhatsNewModal'
import { ImpersonationBanner } from '../impersonation-banner'
import { SmartPreparationBanner } from '../smart-preparation-banner'

const SIDE_NAV_TRANSITION_MS = 240
/**
 * Cadre d'application (essai TEC-1956) : la sidebar et une bande en haut forment un cadre sombre, le contenu
 * est une surface posée dedans, coin haut gauche arrondi, collée à droite et en bas. Le cadre reste fixe : c'est
 * la surface qui défile (`scrollAppToTop` pour la remonter), pas la fenêtre. Les écrans d'édition
 * d'un dossier sont en pleine hauteur (100vh) : seul /declaration retire l'écart (`APP_FRAME_GAP`) de la sienne,
 * l'extraction et la classification restent sans cadre.
 */
const FRAME_GAP_PX = 11
const FRAME_RADIUS_PX = 16

const VerticalLayoutRoot = styled('div')(() => ({
  display: 'flex',
  flex: '1 1 auto',
  maxWidth: '100%',
}))

interface VerticalLayoutProps {
  children?: ReactNode
  navColor?: NavColor
  sections?: Section[]
  changelog: ChangelogReleasesState
  isFullscreen?: boolean
  sideNavExpanded?: boolean
  setSideNavExpanded?: Dispatch<SetStateAction<boolean>>
}

export const VerticalLayout: FC<VerticalLayoutProps> = (props) => {
  const {
    children,
    sections,
    changelog,
    navColor,
    isFullscreen,
    sideNavExpanded = true,
    setSideNavExpanded,
  } = props
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'))
  const mobileNav = useMobileNav()
  const pathname = usePathname()
  const hideSideNavToggleOnDocumentsEditor =
    isDocumentsEditorSidenavPath(pathname)

  const framed = lgUp && !hideSideNavToggleOnDocumentsEditor
  const frameColor = (theme: Theme) =>
    navColor === 'evident'
      ? theme.palette.neutral?.[800] ?? neutral[800]
      : theme.palette.neutral?.[900] ?? neutral[900]
  const isFolded = !sideNavExpanded
  // Repliée ou dépliée par un changement de page (ouvrir une déclaration, en revenir) : la sidenav garde son
  // animation (fixe, elle ne recalcule qu'elle), mais la marge du contenu change d'un coup. L'animer
  // recalculait toute la page qui arrive à chaque image. L'écart ne se voit pas : le cadre a la couleur
  // de la sidenav. Ses libellés se dévoilent pendant l'élargissement au lieu d'attendre la fin.
  const lastLayoutRef = useRef({ pathname, isFolded })
  const foldedByNavigation =
    lastLayoutRef.current.pathname !== pathname &&
    lastLayoutRef.current.isFolded !== isFolded
  useEffect(() => {
    lastLayoutRef.current = { pathname, isFolded }
  })
  const mainPaddingLeft = !isFolded
    ? SIDE_NAV_DRAWER_EXPANDED_PX
    : SIDE_NAV_DRAWER_FOLDED_PX

  return (
    <>
      {/* {!isFullscreen && <TopNav onMobileNavOpen={mobileNav.handleOpen} />} */}
      {/* Le rebond du défilement (macOS) montre le fond de la page au-dessus du cadre : il prend sa couleur. */}
      {framed && (
        <GlobalStyles
          styles={(theme) => ({
            ':root': { [APP_FRAME_GAP_PROPERTY]: `${FRAME_GAP_PX}px` },
            'html, body': { backgroundColor: frameColor(theme as Theme) },
          })}
        />
      )}
      {lgUp && (
        <SideNav
          color={navColor}
          sections={sections}
          changelog={changelog}
          isFolded={isFolded}
          onToggleSideNav={
            setSideNavExpanded && !hideSideNavToggleOnDocumentsEditor
              ? () => setSideNavExpanded((open) => !open)
              : undefined
          }
          transitionDurationMs={SIDE_NAV_TRANSITION_MS}
          revealLabelsImmediately={foldedByNavigation}
          framed={framed}
        />
      )}
      {!lgUp && (
        <MobileNav
          color={navColor}
          onClose={mobileNav.handleClose}
          open={mobileNav.open}
          sections={sections}
        />
      )}

      <VerticalLayoutRoot
        style={{
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          width: '100%',
        }}
        sx={(theme: Theme) => ({
          ...(framed && {
            // Même fond que la sidebar (`--nav-bg` de side-nav.tsx).
            backgroundColor: frameColor(theme),
            paddingTop: `${FRAME_GAP_PX}px`,
            boxSizing: 'border-box',
            height: '100vh',
            overflow: 'hidden',
          }),
          [theme.breakpoints.up('lg')]: {
            paddingLeft: `${mainPaddingLeft}px`,
            transition: theme.transitions.create('padding-left', {
              duration: foldedByNavigation ? 0 : SIDE_NAV_TRANSITION_MS,
              easing: theme.transitions.easing.sharp,
            }),
          },
        })}
      >
        <Box
          {...(framed && { [APP_SCROLL_CONTAINER_ATTR]: '' })}
          style={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            width: '100%',
          }}
          sx={
            framed
              ? (theme: Theme) => ({
                  backgroundColor: theme.palette.background.default,
                  borderTopLeftRadius: `${FRAME_RADIUS_PX}px`,
                  minHeight: 0,
                  overflowY: 'auto',
                  // Colonne qui défile : sans ça, ses enfants rétrécissent quand la page dépasse, et un bandeau
                  // (préparation intelligente, impersonation) déborde de son Collapse sur la page en dessous.
                  '& > *': { flexShrink: 0 },
                  // En sombre, le fond de page et le cadre sont presque du même ton : un trait les sépare.
                  ...(theme.palette.mode === 'dark' && {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                  }),
                })
              : undefined
          }
        >
          <ImpersonationBanner />
          <SmartPreparationBanner changelog={changelog} />
          {children}
        </Box>
      </VerticalLayoutRoot>
    </>
  )
}
